/**
 * WalkVoxels — the walk feature's voxel collision in the editor: holds the
 * sparse voxel grid (scene-core/voxel-grid.ts), its voxel-frame → world
 * transform, and a viewport overlay of the SURFACE voxels.
 *
 * The overlay draws one instanced cube per surface voxel, with only its
 * exposed faces (the rest collapse in the vertex shader), on the gizmo layer
 * like the walk area. Up-facing faces (walkable tops) are green, the rest
 * blue. A whole scene has millions of surface voxels (Damm 8M: 3M in the
 * walked area alone), so it draws a window around the camera — the walk
 * position while previewing, the orbit focal point otherwise — rebuilt when
 * the camera leaves the middle of it.
 *
 * The transform lives on this element's entity, so leveling the scene to a
 * floor moves the voxels with the splats (walk-area.ts levelSceneToFloor).
 * Exported as scene.json `walk.voxels` ({ url, transform }) through
 * WalkArea.serialize; the walk preview and viewer2 build a VoxelCollider
 * from the same two fields. The two files' bytes are kept so a save ships
 * them with the scene (scene-export.ts, as collision/<name>.voxel.*).
 */

import {
    BLEND_NORMAL,
    CULLFACE_NONE,
    Entity,
    Mesh,
    MeshInstance,
    PRIMITIVE_TRIANGLES,
    Ray,
    SEMANTIC_ATTR12,
    SEMANTIC_NORMAL,
    SEMANTIC_POSITION,
    ShaderMaterial,
    TYPE_FLOAT32,
    Vec3,
    VertexBuffer,
    VertexFormat
} from 'playcanvas';

import { Element, ElementType } from '../element';
import type { Events } from '../events';
import type { Scene } from '../scene';
import { VoxelGrid, fetchVoxelFiles, type VoxelBox, type VoxelMeta } from '../../../shared/scene-core/voxel-grid';
import { VoxelCollider } from '../../../shared/scene-core/voxel-walk';
import type { Transform, WalkVoxels as WalkVoxelsConfig } from '../../../shared/scene-core/scene-schema';

// Window drawn around the focus: ±HALF_XZ metres across, ±HALF_Y up/down.
const HALF_XZ = 12;
const HALF_Y = 3.5;
// Rebuild once the focus is this far from the window's centre.
const REBUILD_XZ = 4;
const REBUILD_Y = 2;
// Most cubes drawn; past it the window shrinks until it fits.
const MAX_INSTANCES = 1_500_000;

const vertexShader = /* wgsl */`
attribute vertex_position: vec3f;
attribute vertex_normal: vec3f;
attribute instance_voxel: vec4f;

varying vColor: vec4f;

uniform matrix_model: mat4x4f;
uniform matrix_viewProjection: mat4x4f;
uniform voxel_params: vec4f;
uniform voxel_opacity: f32;

@vertex
fn vertexMain(input: VertexInput) -> VertexOutput {
    var output: VertexOutput;
    let n = input.vertex_normal;
    var bit = 5u;
    if (n.x < -0.5) { bit = 0u; } else if (n.x > 0.5) { bit = 1u; }
    else if (n.y < -0.5) { bit = 2u; } else if (n.y > 0.5) { bit = 3u; }
    else if (n.z < -0.5) { bit = 4u; }
    let faces = u32(input.instance_voxel.w + 0.5);
    if ((faces & (1u << bit)) == 0u) {
        // hidden face: put it outside the clip volume
        output.position = vec4f(2.0, 2.0, 2.0, 1.0);
        return output;
    }
    let res = uniform.voxel_params.w;
    let local = uniform.voxel_params.xyz + (input.instance_voxel.xyz + vec3f(0.5) + input.vertex_position * 0.92) * res;
    output.position = uniform.matrix_viewProjection * uniform.matrix_model * vec4f(local, 1.0);
    let wn = normalize((uniform.matrix_model * vec4f(n, 0.0)).xyz);
    var rgb: vec3f;
    if (wn.y > 0.7) {
        rgb = vec3f(0.35, 0.95, 0.5);
    } else if (wn.y < -0.7) {
        rgb = vec3f(0.25, 0.35, 0.55);
    } else {
        rgb = vec3f(0.4, 0.65, 1.0) * (0.7 + 0.3 * abs(wn.x));
    }
    output.vColor = vec4f(rgb, uniform.voxel_opacity);
    return output;
}
`;

const fragmentShader = /* wgsl */`
varying vColor: vec4f;

@fragment
fn fragmentMain(input: FragmentInput) -> FragmentOutput {
    var output: FragmentOutput;
    output.color = input.vColor;
    return output;
}
`;

/** Unit cube centred on the origin: 6 faces × 4 vertices, outward normals. */
const cubeGeometry = () => {
    const pos: number[] = [];
    const nrm: number[] = [];
    const idx: number[] = [];
    const faces: [number[], number[], number[]][] = [
        // normal, u axis, v axis
        [[-1, 0, 0], [0, 0, 1], [0, 1, 0]],
        [[1, 0, 0], [0, 1, 0], [0, 0, 1]],
        [[0, -1, 0], [1, 0, 0], [0, 0, 1]],
        [[0, 1, 0], [0, 0, 1], [1, 0, 0]],
        [[0, 0, -1], [0, 1, 0], [1, 0, 0]],
        [[0, 0, 1], [1, 0, 0], [0, 1, 0]]
    ];
    for (const [n, u, v] of faces) {
        const base = pos.length / 3;
        for (const [su, sv] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
            for (let k = 0; k < 3; k++) pos.push(0.5 * (n[k] + su * u[k] + sv * v[k]));
            nrm.push(n[0], n[1], n[2]);
        }
        idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
    return { pos, nrm, idx };
};

/** The loaded .voxel.json / .voxel.bin bytes; `name` is the file name without `.voxel.*`. */
type VoxelFiles = { name: string; json: Uint8Array; bin: Uint8Array };

const voxelName = (url: string) => (url.split(/[\/]/).pop() ?? 'collision').replace(/\?.*$/, '').replace(/\.voxel\.(json|bin)$/, '') || 'collision';

class WalkVoxels extends Element {
    entity!: Entity;
    private material!: ShaderMaterial;
    private mesh!: Mesh;
    private meshInstance!: MeshInstance;
    private instanceBuffer: VertexBuffer | null = null;

    private _grid: VoxelGrid | null = null;
    private _url = '';
    private _files: VoxelFiles | null = null;
    private _visible = true;
    private _opacity = 0.6;
    private collider: VoxelCollider | null = null;
    private colliderKey = '';

    // window currently drawn (world-space centre) — NaN forces a rebuild
    private windowCentre = new Vec3(NaN, NaN, NaN);
    private _drawn = 0;

    constructor() {
        super(ElementType.debug);
    }

    add() {
        const { scene } = this;
        const device = scene.graphicsDevice;

        this.material = new ShaderMaterial({
            uniqueName: 'walkVoxelsMaterial',
            attributes: {
                vertex_position: SEMANTIC_POSITION,
                vertex_normal: SEMANTIC_NORMAL,
                instance_voxel: SEMANTIC_ATTR12
            },
            vertexWGSL: vertexShader,
            fragmentWGSL: fragmentShader
        });
        this.material.blendType = BLEND_NORMAL;
        this.material.depthTest = true;
        this.material.depthWrite = true;
        this.material.cull = CULLFACE_NONE;
        this.material.setParameter('voxel_params', [0, 0, 0, 1]);
        this.material.setParameter('voxel_opacity', this._opacity);
        this.material.update();

        const { pos, nrm, idx } = cubeGeometry();
        this.mesh = new Mesh(device);
        this.mesh.setPositions(pos);
        this.mesh.setNormals(nrm);
        this.mesh.setIndices(idx);
        this.mesh.update(PRIMITIVE_TRIANGLES);

        this.meshInstance = new MeshInstance(this.mesh, this.material, null);
        this.meshInstance.cull = false;

        this.entity = new Entity('walkVoxels');
        this.entity.addComponent('render', {
            meshInstances: [this.meshInstance],
            layers: [scene.gizmoLayer.id]
        });
        this.entity.enabled = false;
        scene.app.root.addChild(this.entity);

        registerWalkVoxelsEvents(this, scene.events);
    }

    destroy() {
        this.instanceBuffer?.destroy();
        this.entity?.destroy();
    }

    // ---- state ------------------------------------------------------------
    get grid(): VoxelGrid | null { return this._grid; }
    get url(): string { return this._url; }
    get files(): VoxelFiles | null { return this._files; }
    get visible(): boolean { return this._visible; }
    get drawn(): number { return this._drawn; }

    /** Voxel frame → world, as stored in scene.json. */
    transform(): Transform {
        const r = (n: number) => Math.round(n * 1e5) / 1e5;
        const p = this.entity.getLocalPosition();
        const e = this.entity.getLocalEulerAngles();
        return { position: [r(p.x), r(p.y), r(p.z)], rotation: [r(e.x), r(e.y), r(e.z)] };
    }

    setTransform(t: Transform | undefined) {
        const p = t?.position ?? [0, 0, 0];
        const e = t?.rotation ?? [0, 0, 0];
        this.entity.setLocalPosition(p[0], p[1], p[2]);
        this.entity.setLocalEulerAngles(e[0], e[1], e[2]);
        this.changed();
    }

    /** Called after the entity was moved directly (scene leveling). */
    changed() {
        this.windowCentre.set(NaN, NaN, NaN);
        this.scene.forceRender = true;
        this.scene.events.fire('walk.voxels.changed');
    }

    /** The collider for the current grid + transform (rebuilt when either changes). */
    getCollider(): VoxelCollider | null {
        if (!this._grid) return null;
        const t = this.transform();
        const key = JSON.stringify(t);
        if (!this.collider || key !== this.colliderKey) {
            this.collider = new VoxelCollider(this._grid, t);
            this.colliderKey = key;
        }
        return this.collider;
    }

    setGrid(grid: VoxelGrid | null, url: string, files: VoxelFiles | null = null) {
        this._grid = grid;
        this._url = grid ? url : '';
        this._files = grid ? files : null;
        this.collider = null;
        if (grid) {
            this.material.setParameter('voxel_params', [grid.origin[0], grid.origin[1], grid.origin[2], grid.res]);
        }
        this.changed();
    }

    async loadUrl(url: string, transform?: Transform) {
        this.scene.events.fire('walk.status', 'Loading collision voxels…');
        try {
            const { json, bin } = await fetchVoxelFiles(url);
            const grid = VoxelGrid.fromBuffers(JSON.parse(new TextDecoder().decode(json)) as VoxelMeta, bin);
            if (transform) this.setTransform(transform);
            this.setGrid(grid, url, { name: voxelName(url), json, bin: new Uint8Array(bin) });
            this.scene.events.fire('walk.status', `Collision voxels loaded (${grid.meta.voxelResolution.toFixed(3)} m).`);
        } catch (err) {
            this.scene.events.fire('walk.status', `Collision voxels failed: ${(err as Error).message}`);
            throw err;
        }
    }

    /** Load from a picked .voxel.json + .voxel.bin pair; `url` becomes the json's name. */
    async loadFiles(files: File[]) {
        const json = files.find(f => f.name.endsWith('.voxel.json'));
        const bin = files.find(f => f.name.endsWith('.voxel.bin'));
        if (!json || !bin) {
            this.scene.events.fire('walk.status', 'Pick both the .voxel.json and the .voxel.bin file.');
            return;
        }
        const jsonBytes = new Uint8Array(await json.arrayBuffer());
        const binBytes = await bin.arrayBuffer();
        const grid = VoxelGrid.fromBuffers(JSON.parse(new TextDecoder().decode(jsonBytes)) as VoxelMeta, binBytes);
        this.setGrid(grid, json.name, { name: voxelName(json.name), json: jsonBytes, bin: new Uint8Array(binBytes) });
        this.scene.events.fire('walk.status', `Collision voxels loaded from ${json.name}.`);
    }

    clear() {
        if (!this._grid) return;
        this.setGrid(null, '');
    }

    setVisible(v: boolean) {
        if (v === this._visible) return;
        this._visible = v;
        this.scene.forceRender = true;
        this.scene.events.fire('walk.voxels.visibleChanged', v);
    }

    setOpacity(a: number) {
        this._opacity = Math.max(0.05, Math.min(1, a));
        this.material.setParameter('voxel_opacity', this._opacity);
        this.scene.forceRender = true;
    }

    serialize(): WalkVoxelsConfig | null {
        if (!this._grid || !this._url) return null;
        return { url: this._url, transform: this.transform() };
    }

    // ---- overlay ------------------------------------------------------------
    onPreRender() {
        const show = this._visible && !!this._grid && this.scene.camera.renderOverlays;
        if (show) {
            const focus = this.focus();
            const c = this.windowCentre;
            if (Number.isNaN(c.x) ||
                Math.hypot(focus.x - c.x, focus.z - c.z) > REBUILD_XZ ||
                Math.abs(focus.y - c.y) > REBUILD_Y) {
                this.rebuild(focus);
            }
        }
        this.entity.enabled = show && this._drawn > 0;
    }

    private focus(): Vec3 {
        const cam = this.scene.camera;
        const walking = this.scene.events.invoke('walk.preview.active');
        return walking ? cam.mainCamera.getPosition().clone() : cam.focalPoint.clone();
    }

    /** Voxel-index box covering a world-space box (corners through the inverse transform). */
    private voxelBox(centre: Vec3, halfXZ: number, halfY: number): VoxelBox {
        const grid = this._grid!;
        const inv = this.entity.getWorldTransform().clone().invert();
        const lo = [Infinity, Infinity, Infinity];
        const hi = [-Infinity, -Infinity, -Infinity];
        const p = new Vec3();
        for (let i = 0; i < 8; i++) {
            p.set(
                centre.x + ((i & 1) ? halfXZ : -halfXZ),
                centre.y + ((i & 2) ? halfY : -halfY),
                centre.z + ((i & 4) ? halfXZ : -halfXZ)
            );
            inv.transformPoint(p, p);
            const v = grid.indexOf(p.x, p.y, p.z);
            for (let k = 0; k < 3; k++) {
                lo[k] = Math.min(lo[k], v[k]);
                hi[k] = Math.max(hi[k], v[k] + 1);
            }
        }
        const clamp = (v: number) => Math.max(0, Math.min(grid.size, v));
        return {
            min: [clamp(lo[0]), clamp(lo[1]), clamp(lo[2])],
            max: [clamp(hi[0]), clamp(hi[1]), clamp(hi[2])]
        };
    }

    private rebuild(centre: Vec3) {
        const grid = this._grid!;
        this.windowCentre.copy(centre);
        let half = HALF_XZ;
        let surf = grid.surfaceVoxels(this.voxelBox(centre, half, HALF_Y), MAX_INSTANCES);
        while (surf.truncated && half > 2) {
            half *= 0.75;
            surf = grid.surfaceVoxels(this.voxelBox(centre, half, HALF_Y), MAX_INSTANCES);
        }
        const count = surf.count;
        const data = new Float32Array(count * 4);
        for (let i = 0; i < count * 4; i++) data[i] = surf.voxels[i];

        this.instanceBuffer?.destroy();
        this.instanceBuffer = null;
        this._drawn = count;
        if (count === 0) {
            this.meshInstance.setInstancing(null);
            return;
        }
        const device = this.scene.graphicsDevice;
        const format = new VertexFormat(device, [
            { semantic: SEMANTIC_ATTR12, components: 4, type: TYPE_FLOAT32 }
        ]);
        this.instanceBuffer = new VertexBuffer(device, format, count, { data });
        this.meshInstance.setInstancing(this.instanceBuffer);
        this.scene.forceRender = true;
        this.scene.events.fire('walk.voxels.drawn', count, half);
    }
}

// ----------------------------------------------------------------------------
// Event wiring — `walk.voxels.*`.
// ----------------------------------------------------------------------------

const registerWalkVoxelsEvents = (vox: WalkVoxels, events: Events) => {
    events.function('walk.voxels', () => vox);
    events.function('walk.voxels.collider', () => vox.getCollider());
    events.function('walk.voxels.serialize', () => vox.serialize());
    events.function('walk.voxels.files', () => vox.files);
    events.function('walk.voxels.visible', () => vox.visible);
    // Where the camera ray through a normalized screen point (0-1) first meets
    // the voxels, while they are shown; null otherwise. The measure tool uses
    // it: the splat depth pick sees through plain white walls, the voxels don't.
    const pickRay = new Ray();
    events.function('walk.voxels.pick', (x: number, y: number) => {
        const collider = vox.visible ? vox.getCollider() : null;
        if (!collider) return null;
        const { canvas, camera } = vox.scene;
        camera.getRay(x * canvas.clientWidth, y * canvas.clientHeight, pickRay);
        const o = pickRay.origin, d = pickRay.direction;
        const t = collider.raycast([o.x, o.y, o.z], [d.x, d.y, d.z], camera.far);
        return Number.isNaN(t) ? null : { position: new Vec3(o.x + d.x * t, o.y + d.y * t, o.z + d.z * t), distance: t };
    });

    events.on('walk.voxels.load', (url: string, transform?: Transform) => {
        vox.loadUrl(url, transform).catch(() => { /* reported via walk.status */ });
    });
    events.on('walk.voxels.loadFiles', (files: File[]) => {
        vox.loadFiles(files).catch((err: Error) => events.fire('walk.status', `Collision voxels failed: ${err.message}`));
    });
    events.on('walk.voxels.transform', (t: Transform) => vox.setTransform(t));
    events.on('walk.voxels.setVisible', (v: boolean) => vox.setVisible(v));
    events.on('walk.voxels.opacity', (a: number) => vox.setOpacity(a));
    events.on('walk.voxels.clear', () => vox.clear());
    // .ssproj / scene.json round-trip: the walk block carries { url, transform }
    events.on('walk.voxels.deserialize', (data: WalkVoxelsConfig | null) => {
        if (data && typeof data.url === 'string' && data.url) {
            if (data.url !== vox.url || !vox.grid) events.fire('walk.voxels.load', data.url, data.transform);
            else vox.setTransform(data.transform);
        }
    });

    events.on('scene.clear', () => vox.clear());
};

export { WalkVoxels, type VoxelFiles };
